
import React from 'react';
import { Assessment, Category } from '@/types/assessment';
import { CardContent } from '@/components/ui/card';
import AssessmentResultsChart from '../AssessmentResultsChart';
import RecommendationsSection from '../RecommendationsSection';
import { MaturityScoreResult } from '@/types/assessment';
import MaturityScoreSection from './MaturityScoreSection';
import NextStepsSection from './NextStepsSection';
import { generateStandalonePDF } from '@/utils/pdfGenerator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AssessmentCompleteContentProps {
  assessment: Assessment;
  categories: Category[];
  scoresByCategory: Record<string, {current: number, target: number}>;
  maturityResults: { current: MaturityScoreResult, target: MaturityScoreResult };
  recommendations: any[];
}

const AssessmentCompleteContent = ({ 
  assessment, 
  categories, 
  scoresByCategory, 
  maturityResults,
  recommendations
}: AssessmentCompleteContentProps) => {
  
  // Handle PDF download using the same method as email
  const handleDownloadReport = async () => {
    try {
      toast.info('Generating PDF report...');
      
      // Generate PDF using the same method as email
      const pdfBlob = await generateStandalonePDF(
        assessment,
        categories,
        scoresByCategory,
        maturityResults.current,
        maturityResults.target,
        recommendations
      );
      
      if (!pdfBlob) {
        toast.error('Failed to generate PDF report');
        return;
      }
      
      const filename = `${assessment.title} - Results - ${Date.now()}.pdf`;
      
      // Download locally
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('PDF report downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  return (
    <CardContent className="pt-0">
      <div className="space-y-8">
        {/* Maturity Score Cards */}
        <MaturityScoreSection 
          currentMaturity={maturityResults.current}
          targetMaturity={maturityResults.target}
        />

        {/* Results Chart */}
        <AssessmentResultsChart 
          categories={categories} 
          scoresByCategory={scoresByCategory} 
        />
        
        {/* Recommendations Section */}
        {recommendations.length > 0 ? (
          <div className="border-t pt-6">
            <RecommendationsSection 
              recommendations={recommendations} 
              onExport={handleDownloadReport}
            />
          </div>
        ) : (
          <div className="border-t pt-6 text-center py-4">
            <p className="text-muted-foreground">No specific recommendations available.</p>
          </div>
        )}

        {/* What happens next section */}
        <NextStepsSection />
      </div>
    </CardContent>
  );
};

export default AssessmentCompleteContent;
